import { keycloak } from "@/plugins/auth";

type HeaderOptions = {
  contentType?: string | null;
  withAuth?: boolean;
  accept?: string | null;
  prefer?: string | null;
};

/**
 * Builds HTTP headers for API requests.
 *
 * Defaults to JSON content type, JSON response format, the current Keycloak
 * access token, and asking PostgREST to return the rows a write affected.
 * Pass `null` to omit `contentType`, `accept` or `prefer`, or
 * `withAuth: false` to omit authentication.
 *
 * `prefer` is not cosmetic: PostgREST answers a write with an empty body
 * unless the request asks for `return=representation`, and the generated
 * client types writes as returning their rows. Dropping the header makes
 * those types lie. See tools/postgrest-openapi/README.md.
 */
export function buildHeaders({
  contentType = "application/json",
  withAuth = true,
  accept = "application/json",
  prefer = "return=representation",
}: HeaderOptions = {}) {
  const headers = new Headers();

  if (contentType) {
    headers.set("Content-Type", contentType);
  }

  if (withAuth) {
    headers.set("Authorization", `Bearer ${keycloak.token}`);
  }

  if (accept) {
    headers.set("Accept", accept);
  }

  if (prefer) {
    headers.set("Prefer", prefer);
  }

  return headers;
}

/**
 * Converts a `Headers` instance to a plain object.
 *
 * An orval-generated mutation call (POST/PATCH) merges `options.headers` via
 * `{ 'Content-Type': ..., ...options?.headers }` - spreading a `Headers`
 * instance there copies none of its entries (it has no own enumerable
 * properties), which silently drops `Authorization`/`Accept`/`Prefer` from
 * the request. Passing `headersToObject(buildHeaders())` instead of
 * `buildHeaders()` directly into such a call keeps the merge working. GET
 * and DELETE calls spread the whole `options` object instead and aren't
 * affected, so this is only needed for POST/PATCH.
 */
export function headersToObject(headers: Headers): Record<string, string> {
  return Object.fromEntries(headers.entries());
}
